/**
 * Scrub URL-embedded secrets from arbitrary text.
 *
 * Query params `auth_token`, `token`, `key`, `claim`, `code`, `state`
 * (case-insensitive) carry session tokens, magic-link keys, SSO nonces and
 * OAuth state; CloudFront (`Signature`, `Key-Pair-Id`, `Expires`, `Policy`)
 * and S3 presign (`X-Amz-*`) params sign file URLs (#768). None may persist
 * in alert rows or digests. This module
 * replaces their VALUES with `<redacted>` in any string — URLs, stack traces,
 * breadcrumb messages, serialised JSON — without altering non-secret params or
 * route templates.
 *
 * Also rewrites magic-link path segments:
 *   `/auth/link/<digits>/<key>/` → `/auth/link/<digits>/<redacted>/`
 *
 * Pure, idempotent (`<redacted>` in → `<redacted>` out), and recursive over
 * nested objects/arrays via `scrubDeep`.
 *
 * Mirrors vingapi `src/core/tracing/scrub_url.py` (pinned by
 * `tests/core/tracing/test_scrub_url.py`).
 *
 * Issue #530 (client slice of vingapi#868).
 */

/**
 * Query params whose VALUE is always a secret. Matched case-insensitively.
 *
 * Client ⊇ server: covers every name in vingapi `SECRET_QUERY_FAMILIES`
 * (`src/core/tracing/scrub_url.py`), pinned by
 * `test_client_scrub_covers_every_server_secret_family` against the committed
 * contract copy `docs/contracts/secret_query_families.vingapi-9a2fc6e6c.json`.
 */
const SECRET_QUERY_PARAMS = [
  // session_token
  'auth_token',
  'token',
  // link_credential
  'key',
  'claim',
  // oauth
  'code',
  'state',
  // cloudfront (signed file URLs, #768)
  'signature',
  'key-pair-id',
  'expires',
  'policy',
  // s3_presign (#768) — also covered by the x-amz- prefix rule below
  'x-amz-signature',
  'x-amz-credential',
  'x-amz-security-token',
  'x-amz-algorithm',
  'x-amz-date',
  'x-amz-expires',
  'x-amz-signedheaders',
] as const;

/**
 * Param-name PREFIXES whose every member is a secret: any `X-Amz-*` param.
 * A deliberate superset of the server's s3_presign family, so a future AWS
 * signing param is redacted before anyone lists it.
 */
const SECRET_QUERY_PREFIXES = ['x-amz-'] as const;

// Build one regex matching `?param=VALUE`, `&param=VALUE`, or `#param=VALUE`
// for each secret param name. VALUE runs up to the next `&`, `#`, whitespace,
// quote, or end-of-string.
//
// The `[?&#]` prefix distinguishes a URL query param from a plain English
// phrase or a compound key: `error_code=cancelled` has no delimiter before
// `code`, so it does not match; `?code=xyz` does. The literal `=` after the
// name means a name that merely CONTAINS a secret name (`keyword=`,
// `expiresIn=`) never matches.
//
// Alternation is longest-first, as on the server, so `key-pair-id` is taken
// whole rather than as `key` + a leftover.
const _escape = (p: string) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const _paramAlts = [
  ...SECRET_QUERY_PREFIXES.map((p) => `${_escape(p)}[^=&#\\s'"]+`),
  ...[...SECRET_QUERY_PARAMS].sort((a, b) => b.length - a.length).map(_escape),
].join('|');

const SECRET_PARAM_RE = new RegExp(
  '([?&#])' +               // group 1: query/fragment delimiter
  '(' + _paramAlts + ')' +   // group 2: param name
  '=' +                      // literal =
  '([^&#\\s\'"]*)',          // group 3: the value (up to terminator)
  'gi',
);

// Magic-link path: `/auth/link/<digits>/<key>/` (trailing slash optional).
// The key segment is everything after the digits segment up to the next `/`
// or end. `<redacted>` is never rewritten (idempotent).
const MAGIC_LINK_RE = /(\/auth\/link\/\d+\/)(?!<redacted>)([^/?#\s]+)/g;

/**
 * Replace secret query-param values and magic-link keys with `<redacted>`.
 *
 * Works on arbitrary text (stack traces, JSON, breadcrumb messages).
 * Idempotent: passing already-scrubbed text returns it unchanged.
 */
export function scrubUrlSecrets(text: string): string {
  // Reset lastIndex for global regexes (they are module-level and stateful).
  SECRET_PARAM_RE.lastIndex = 0;
  MAGIC_LINK_RE.lastIndex = 0;

  // Phase 1: secret query params.
  let result = text.replace(
    SECRET_PARAM_RE,
    (_match, delimiter: string, paramName: string, value: string) => {
      if (!value || value === '<redacted>') return `${delimiter}${paramName}=${value}`;
      return `${delimiter}${paramName}=<redacted>`;
    },
  );

  // Phase 2: magic-link path key.
  result = result.replace(MAGIC_LINK_RE, '$1<redacted>');

  return result;
}

/**
 * Recursively scrub secrets in strings within objects, arrays, or bare strings.
 *
 * Non-string leaves are returned as-is. Does not mutate the input.
 */
export function scrubDeep<T>(value: T): T {
  if (typeof value === 'string') {
    return scrubUrlSecrets(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item: unknown) => scrubDeep(item)) as T;
  }
  if (typeof value === 'object' && value !== null) {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = scrubDeep(v);
    }
    return result as T;
  }
  return value;
}
